// src/features/riskmize/IncidentPopup.jsx
//
// Anchored card that floats over the map pin the user clicked. Portal'd to
// document.body to escape the glass-panel stacking context (same idiom as
// PhotoLightbox / ConfirmDialog). Closes on Escape, outside click, or the
// explicit X button. Clicking "View details" dispatches selectIncident(id),
// which flips the drawer into detail mode.
//
// @param {{ incident: object, anchor: {x:number,y:number}, onClose: () => void }} props
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { X, MapPin, Clock, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTimePlugin from 'dayjs/plugin/relativeTime';
import { riskmizeActions } from '@/store';
import { SEVERITY_COLORS } from './severity';

dayjs.extend(relativeTimePlugin);

// Label text color is white across all severities — kept local because it's
// purely presentational and doesn't belong in the shared constants module.
const SEVERITY_LABEL_CLASS = 'text-white';

const POPUP_WIDTH = 280;

export default function IncidentPopup({ incident, anchor, onClose }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // Defer the click binding by one tick so the click that opened the
    // popup doesn't immediately close it.
    const id = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      clearTimeout(id);
    };
  }, [onClose]);

  if (!incident) return null;

  const severity = incident.severity || 'low';
  const severityColor = SEVERITY_COLORS[severity] || SEVERITY_COLORS.low;
  const when = incident.incident_date || incident.created_at;

  // Position: place the card above+right of the anchor, but clamp so it
  // never renders off-screen (viewport bounds-aware).
  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const left = Math.max(8, Math.min(anchor.x + 12, vw - POPUP_WIDTH - 8));
  const top  = Math.max(8, Math.min(anchor.y - 80, vh - 8));

  const node = (
    <div
      ref={ref}
      className="fixed z-[60] rounded-2xl shadow-xl border border-white/30 dark:border-slate-700/50 overflow-hidden bg-white dark:bg-slate-900"
      style={{ left, top, width: POPUP_WIDTH }}
      role="dialog"
    >
      {/* Thumb — image or severity-colored banner fallback */}
      <div className="relative h-24 w-full">
        {incident.image_url ? (
          <img
            src={incident.image_url}
            alt=""
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <div className="h-full w-full" style={{ background: severityColor }} />
        )}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-1.5 end-1.5 p-1 rounded-lg bg-black/50 text-white hover:bg-black/70 transition-colors"
          aria-label={t('common.close', 'Close')}
        >
          <X size={14} />
        </button>
      </div>

      <div className="p-3 space-y-2">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 line-clamp-2">
          {incident.title || t('riskmize.untitledIncident', 'Untitled incident')}
        </h3>

        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${SEVERITY_LABEL_CLASS}`}
            style={{ background: severityColor }}
          >
            {severity}
          </span>
          {incident.category_name && (
            <span className="text-[10px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md">
              {incident.category_name}
            </span>
          )}
        </div>

        {(incident.province || incident.country) && (
          <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin size={11} />
            <span className="truncate">
              {[incident.province, incident.country].filter(Boolean).join(', ')}
            </span>
          </div>
        )}

        {when && (
          <div className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <Clock size={11} />
            <span>{dayjs(when).fromNow()}</span>
          </div>
        )}

        {incident.description && (
          <p className="text-[11px] text-slate-600 dark:text-slate-300 line-clamp-2">
            {incident.description}
          </p>
        )}

        <button
          type="button"
          onClick={() => {
            dispatch(riskmizeActions.selectIncident(incident.id));
            // Close popup on "View details" so the drawer's detail view owns the screen (see Phase 5 review).
            onClose();
          }}
          className="mt-1 w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg py-1.5 transition-colors"
        >
          {t('riskmize.viewDetails', 'View details')}
          <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
